import { type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Form';

/**
 * Confirmation with embedded form. For simple yes/no, use ConfirmDialog.
 * Props: onSubmit fires on accept, onClose fires on dismiss.
 */

// Plan-field-service-orders.md follow-up — confirmation modal
// pattern shared by admin/cancel actions on the SO inbox.
//
// This wraps the generic `Modal` primitive with the layout
// pieces every admin/cancel flow needs:
//
//   1. A "source context" block — what's about to change (current
//      status, current customer, etc.). Operators noted that the
//      old window.prompt sequences didn't surface this and they
//      had to back-and-forth between the prompt and the detail
//      rail to confirm they were operating on the right row.
//
//   2. A "form body" slot — caller's inputs (reason textarea,
//      customer/store cuid inputs, etc.). Each operation has its
//      own field shape, so we don't try to unify them here.
//
//   3. A consistent footer — 취소 (always closes without firing)
//      + a primary action button (label/variant per operation,
//      can disable when caller's form is invalid, shows a loading
//      pill while the mutation is in flight).
//
// ESC, the X button in the header, and the 취소 footer button all
// call `onClose`. Backdrop click is intentionally NOT a dismiss
// (Plan §G-1 — operators lose work when accidental backdrop clicks
// dismiss modals mid-typing).

export interface ConfirmActionModalProps {
  /** Whether the modal is rendered. */
  open: boolean;

  /**
   * Called when the user dismisses (ESC, X, or 취소 footer). Should
   * unmount the modal and reset any form state in the caller. NOT
   * called on successful submit — the caller's mutation onSuccess
   * is the right place to close.
   */
  onClose: () => void;

  /** Modal header text. */
  title: string;

  /**
   * Optional context block rendered above the form body. Use this
   * to surface the current row's values (status, humanId, current
   * customer/store) so the operator can sanity-check the target
   * before submitting.
   */
  context?: ReactNode;

  /** The form inputs themselves. Rendered inside the modal body. */
  children: ReactNode;

  /** Footer primary-action label. e.g. "복구", "재배정", "취소 처리". */
  submitLabel: string;

  /**
   * Footer primary-action variant. Defaults to 'primary'. Use
   * 'danger' for destructive ops (SO cancellation, etc.) to give
   * the operator visual reinforcement of irreversibility.
   */
  submitVariant?: 'primary' | 'danger';

  /**
   * When true, the primary action is disabled. Used for caller-
   * side form validation (e.g. empty reason field).
   */
  submitDisabled?: boolean;

  /**
   * When true, the primary action shows a loading state and is
   * not clickable. Caller wires this to the mutation's `isPending`.
   */
  isSubmitting?: boolean;

  /**
   * Fired when the user clicks the primary action. Caller should
   * dispatch the mutation here. Not called when `submitDisabled`
   * or `isSubmitting`.
   */
  onSubmit: () => void;
}

export function ConfirmActionModal({
  open,
  onClose,
  title,
  context,
  children,
  submitLabel,
  submitVariant = 'primary',
  submitDisabled = false,
  isSubmitting = false,
  onSubmit,
}: ConfirmActionModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="confirm-action-cancel"
          >
            취소
          </Button>
          <Button
            type="button"
            variant={submitVariant}
            disabled={submitDisabled || isSubmitting}
            onClick={() => {
              if (submitDisabled || isSubmitting) return;
              onSubmit();
            }}
            data-testid="confirm-action-submit"
          >
            {isSubmitting ? '처리 중…' : submitLabel}
          </Button>
        </>
      }
    >
      {context && (
        <div
          style={{
            background: 'var(--bg-muted)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            marginBottom: 12,
            fontSize: 12,
            color: 'var(--fg-muted)',
          }}
          data-testid="confirm-action-context"
        >
          {context}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </Modal>
  );
}
